// Client-side session identity for the lightweight (SDK-less) analytics beacon.
// PostHog Web Analytics is session-based: every `$pageview` / `$pageleave` needs
// a stable `$session_id` that rotates the way posthog-js rotates it, or the
// sessions table never forms and the dashboard reads ~0.
//
// Session id  → localStorage, shared across tabs, rotates after 30 min of
//               inactivity or 24 h of age (matches posthog-js defaults).
// Window id   → sessionStorage, one per tab for its lifetime.
import { uuidv7 } from './uuid'

const SESSION_KEY = 'ht_ph_session'
const WINDOW_KEY = 'ht_ph_window'
const IDLE_MS = 30 * 60 * 1000
const MAX_MS = 24 * 60 * 60 * 1000

interface StoredSession {
  id: string
  startTs: number
  lastTs: number
}

const readSession = (): StoredSession | null => {
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as Partial<StoredSession>
    if (typeof parsed.id === 'string' && typeof parsed.startTs === 'number' && typeof parsed.lastTs === 'number') {
      return { id: parsed.id, startTs: parsed.startTs, lastTs: parsed.lastTs }
    }
    return null
  } catch {
    return null
  }
}

const writeSession = (session: StoredSession): void => {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session))
  } catch {
    // Private mode / storage disabled — degrade to a fresh per-call id rather
    // than throw into the analytics path.
  }
}

const getWindowId = (): string => {
  try {
    const existing = sessionStorage.getItem(WINDOW_KEY)
    if (existing) {
      return existing
    }
    const fresh = uuidv7()
    sessionStorage.setItem(WINDOW_KEY, fresh)
    return fresh
  } catch {
    return uuidv7()
  }
}

// Returns the active session/window ids, rotating the session if it has gone
// idle or aged out. Each call also "touches" the session (extends the idle
// window), so calling it on every pageview/pageleave keeps an active visit alive.
export const getSessionContext = (): { sessionId: string; windowId: string } => {
  const now = Date.now()
  const stored = readSession()
  const expired = !stored || now - stored.lastTs > IDLE_MS || now - stored.startTs > MAX_MS

  const session: StoredSession = expired ? { id: uuidv7(), startTs: now, lastTs: now } : { ...stored, lastTs: now }
  writeSession(session)

  return { sessionId: session.id, windowId: getWindowId() }
}
